"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext } from "react";
import { BiCheckDouble } from "react-icons/bi";
import { FaCheck, FaTriangleExclamation } from "react-icons/fa6";
import { HiOutlineMail, HiOutlineMailOpen } from "react-icons/hi";
import { TbMailForward, TbMailShare } from "react-icons/tb";
import { ChatBubble } from "@/components/chat/chat-bubble";
import { useChatConfigContextOptional } from "@/components/chat/contexts/chat-config-context";
import { useChatData } from "@/components/chat/hooks";
import AIBadge from "@/components/ui/ai-badge";
import Badge from "@/components/ui/badge";
import CollapsibleText from "@/components/ui/collapsible-text";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Tooltip from "@/components/ui/tooltip";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { type ClinicClock, clinicDaysAgo } from "@/lib/clinic-clock";
import { formatDate, formatStaffName, formatTime, formatWeekdayShort } from "@/lib/format-data";
import { tv } from "@/lib/tailwind-utils";
import { cx } from "@/lib/tailwind-utils";
import { CallDetail, ChatEmailSummary } from "@/types/chat";
import { UserType } from "@/types/enums";
import { MessageType } from "@/types/enums/message";
import { MessageStatus } from "@/types/enums/message";
import { MessageSummary } from "@/types/message";
import { MessageCall } from "./message-call";
import { MessageEmailContent } from "./message-email";
import { MessageFiles } from "./message-files";
import { MessageImages } from "./message-images";
import { MessageText } from "./message-text";
import { MessageToolResult } from "./message-tool-result";
const messageStyles = tv({
  slots: {
    wrapper: "flex w-full flex-col",
    header: "flex w-full items-center",
    senderName: "font-bold truncate min-w-0",
    callBadge: "ml-1.5 select-none rounded-lg bg-gradient-to-tr from-green-400/40 to-primary/70 text-white shrink-0",
    webChatBadge: "ml-1.5 select-none rounded-md border border-white/20 bg-white/15 font-medium text-white shrink-0",
    portalBadge: "ml-1.5 select-none rounded-md border font-medium shrink-0",
    timestamp: "font-light shrink-0",
    content: "break-words overflow-hidden",
    statusWrapper: "mt-1 flex w-full flex-row text-right text-tiny",
    notificationWrapper: "mt-1 flex w-full flex-row text-tiny"
  },
  variants: {
    size: {
      sm: {
        callBadge: "text-micro p-0.5 px-1",
        webChatBadge: "text-micro p-0.5 px-1",
        portalBadge: "text-micro p-0.5 px-1",
        timestamp: "text-tiny",
        content: "mt-1"
      },
      lg: {
        callBadge: "text-tiny p-1 px-1.5",
        webChatBadge: "text-tiny p-1 px-1.5",
        portalBadge: "text-tiny p-1 px-1.5",
        timestamp: "ml-2 text-caption",
        content: "mt-1.5",
        header: "gap-1"
      }
    },
    isPatient: {
      true: {
        // Inbound bubble is bg-primary and forces every descendant to
        // primary-foreground, so the badge must be tinted against that.
        portalBadge: "border-primary-foreground/30 bg-primary-foreground/15 !text-primary-foreground",
        statusWrapper: "justify-start ml-2",
        notificationWrapper: "justify-start ml-2"
      },
      false: {
        portalBadge: "border-primary/30 bg-primary/15 !text-primary",
        senderName: "text-primary",
        statusWrapper: "justify-end mr-2",
        notificationWrapper: "justify-end mr-2"
      }
    }
  },
  defaultVariants: {
    size: "lg"
  }
});

// Union type for all message types
export type AnyMessage = MessageSummary | CallDetail | ChatEmailSummary;

// Type guards
export function isEmail(message: AnyMessage): message is ChatEmailSummary {
  return "threadId" in message && "direction" in message;
}
export function isCall(message: AnyMessage): message is CallDetail {
  return "completedDate" in message;
}
export function isMessageSummary(message: AnyMessage): message is MessageSummary {
  return "status" in message && "messageUid" in message;
}

// Context for sharing message state with child components
interface MessageContextValue {
  message: AnyMessage;
  isLastMessage: boolean;
  size: "sm" | "lg";
  isInbound: boolean;
}
const MessageContext = createContext<MessageContextValue | null>(null);
export function useMessageContext() {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error("useMessageContext must be used within a Message component");
  }
  return context;
}

// Email badge component for email headers
function EmailBadge(t0) {
  const $ = _c(10);
  const {
    email,
    size
  } = t0;
  const isInbound = email.direction === "INBOUND";
  const isRead = email.status === "DELIVERED" || email.status === "RECEIVED" || email.status === "SENT";
  const EmailIcon = isInbound ? isRead ? HiOutlineMailOpen : HiOutlineMail : isRead ? TbMailShare : TbMailForward;
  const t1 = size === "sm" ? "px-2 py-0.5 text-tiny" : "px-2.5 py-1 text-caption";
  const t2 = isInbound ? "bg-status-purple-bg text-status-purple-text" : "bg-status-indigo-bg text-status-indigo-text";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cx("inline-flex items-center gap-1.5 rounded-full", t1, "font-medium tracking-wide uppercase", t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";
  let t5;
  if ($[3] !== EmailIcon || $[4] !== t4) {
    t5 = <EmailIcon className={t4} />;
    $[3] = EmailIcon;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span>Email</span>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t3 || $[8] !== t5) {
    t7 = <div className={t3}>{t5}{t6}</div>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}

// Format date for display
function formatMessageDate(createdDate: string, clock: ClinicClock) {
  // Today and this year are the clinic's, as the times beside them are.
  const zone = clock.timeZone;
  const messageDate = new Date(createdDate);
  const isToday = clinicDaysAgo(messageDate, clock) === 0;
  const isCurrentYear = clock.dayOf(messageDate).slice(0, 4) === clock.today().slice(0, 4);
  const time = formatTime(messageDate, true, zone);
  if (isToday) return time;
  const day = formatDate(messageDate, {
    month: "short",
    day: "numeric",
    year: isCurrentYear ? undefined : "numeric",
    timeZone: zone
  });
  return `${formatWeekdayShort(messageDate, zone)}, ${day}, ${time}`;
}

// Internal MessageHeader component
// A shared number's thread mixes portal messages for several patients.
function PortalRecipientBadge(t0) {
  const $ = _c(10);
  const {
    patientId,
    patientName,
    className
  } = t0;
  const chatConfig = useChatConfigContextOptional();
  const t1 = chatConfig?.chatId ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = {
      chatId: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    chat
  } = useChatData(t2);
  let t3;
  let t4;
  if ($[2] !== chat?.patients || $[3] !== patientId || $[4] !== patientName) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const participant = chat?.patients?.find(p => p.id === patientId);
      if (participant && (chat?.patients?.length ?? 0) < 2) {
        t4 = null;
        break bb0;
      }
      t3 = participant ? formatStaffName(participant) : patientName;
    }
    $[2] = chat?.patients;
    $[3] = patientId;
    $[4] = patientName;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  const name = t3;
  if (!name) {
    return null;
  }
  const t5 = `To ${name}`;
  let t6;
  if ($[7] !== className || $[8] !== t5) {
    t6 = <Tooltip content="Only this patient sees it in their portal"><span className={className}>{t5}</span></Tooltip>;
    $[7] = className;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function MessageHeader() {
  const $ = _c(24);
  const {
    message,
    size,
    isInbound
  } = useMessageContext();
  const clock = useClinicClock();
  let t0;
  if ($[0] !== clock || $[1] !== message.createdDate) {
    t0 = formatMessageDate(message.createdDate, clock);
    $[0] = clock;
    $[1] = message.createdDate;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const formattedDate = t0;
  let t1;
  let t2;
  if ($[3] !== formattedDate || $[4] !== isInbound || $[5] !== message || $[6] !== size) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const styles = messageStyles({
        size,
        isPatient: isInbound
      });
      if (isEmail(message)) {
        let t3;
        if ($[9] !== message || $[10] !== size) {
          t3 = <EmailBadge email={message} size={size} />;
          $[9] = message;
          $[10] = size;
          $[11] = t3;
        } else {
          t3 = $[11];
        }
        const t4 = size === "lg" && <div className={styles.header()}><span className={styles.timestamp()}>{formattedDate}</span></div>;
        const t5 = size === "sm" && <span className={styles.timestamp()}>{formattedDate}</span>;
        let t6;
        if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
          t6 = <div className="flex items-center gap-1 justify-start">{t3}{t4}{t5}</div>;
          $[12] = t3;
          $[13] = t4;
          $[14] = t5;
          $[15] = t6;
        } else {
          t6 = $[15];
        }
        t2 = t6;
        break bb0;
      }
      let t3;
      if ($[16] !== message) {
        t3 = message.sender ? formatStaffName(message.sender) : isCall(message) && message.originatingDeskPhone ? message.originatingDeskPhone.displayName : "Unknown";
        $[16] = message;
        $[17] = t3;
      } else {
        t3 = $[17];
      }
      const senderLabel = t3;
      let t4;
      if ($[18] !== message) {
        t4 = isCall(message) && message.sender ? message.direction === "INBOUND" ? message.answeredViaDeskPhone?.displayName : message.originatingDeskPhone?.displayName : undefined;
        $[18] = message;
        $[19] = t4;
      } else {
        t4 = $[19];
      }
      const deskPhoneLabel = t4;
      let t5;
      if ($[20] !== message.sender?.type) {
        t5 = message.sender?.type === UserType.ASSISTANT && <AIBadge />;
        $[20] = message.sender?.type;
        $[21] = t5;
      } else {
        t5 = $[21];
      }
      let t6;
      if ($[22] !== message) {
        t6 = isMessageSummary(message) && message.type === MessageType.WAITLIST && <Badge variant="default" size="xs">Waitlist</Badge>;
        $[22] = message;
        $[23] = t6;
      } else {
        t6 = $[23];
      }
      t1 = <><div className={styles.header()}>{t5}<span className={styles.senderName()}>{senderLabel}</span>{t6}{isCall(message) && <span className={styles.callBadge()}>Call</span>}{deskPhoneLabel && <span className={styles.callBadge()}>{deskPhoneLabel}</span>}{isMessageSummary(message) && message.channel === "web_chat" && <Tooltip content="Sent from the clinic's website chat widget"><span className={styles.webChatBadge()}>Web</span></Tooltip>}{isMessageSummary(message) && message.channel === "patient_portal" && <Tooltip content="Sent through the secure patient portal"><span className={styles.portalBadge()}>Portal</span></Tooltip>}{isMessageSummary(message) && message.recipientPatientId && <PortalRecipientBadge patientId={message.recipientPatientId} patientName={message.recipientPatientName} className={styles.portalBadge()} />}{size === "lg" && <span className={styles.timestamp()}>{formattedDate}</span>}</div>{size === "sm" && <span className={styles.timestamp()}>{formattedDate}</span>}</>;
    }
    $[3] = formattedDate;
    $[4] = isInbound;
    $[5] = message;
    $[6] = size;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return t1;
}

// Collapsed record of the out-of-band ping that announced a portal message.
//
// Lives under the bubble it announced rather than as its own thread entry, so
// staff read "the patient was told" as a property of the message instead of
// scrolling past a second, contentless row.
function PortalNotificationNotice() {
  const $ = _c(22);
  const {
    message,
    isInbound
  } = useMessageContext();
  if (!isMessageSummary(message)) {
    return null;
  }
  const notification = message.portalNotification;
  const unnotifiedReason = !notification && !isInbound && message.channel === "patient_portal" ? message.errorMessage : null;
  if (!notification && !unnotifiedReason) {
    return null;
  }
  const failure = !!unnotifiedReason || notification?.status === MessageStatus.FAILED || notification?.status === MessageStatus.UNDELIVERED;
  let detail;
  let t0;
  if ($[0] !== failure || $[1] !== isInbound || $[2] !== notification?.content || $[3] !== notification?.errorMessage || $[4] !== unnotifiedReason) {
    const styles = messageStyles({
      isPatient: isInbound
    });
    detail = unnotifiedReason ?? (failure ? notification?.errorMessage ?? "The text message telling this patient about the portal message was not delivered." : notification?.content);
    t0 = styles.notificationWrapper();
    $[0] = failure;
    $[1] = isInbound;
    $[2] = notification?.content;
    $[3] = notification?.errorMessage;
    $[4] = unnotifiedReason;
    $[5] = detail;
    $[6] = t0;
  } else {
    detail = $[5];
    t0 = $[6];
  }
  const t1 = unnotifiedReason ? "Patient was NOT notified" : failure ? "Text notification failed" : "text message" ? "Notification sent by text message" : "Notification sent";
  let t2;
  if ($[7] !== failure) {
    t2 = failure ? <FaTriangleExclamation /> : <FaCheck />;
    $[7] = failure;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = failure ? "font-semibold text-status-error-text" : "text-foreground-muted";
  let t4;
  if ($[9] !== t3) {
    t4 = cx(t3);
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== detail || $[12] !== failure) {
    t5 = detail && <p data-testid={failure ? "portal-notification-reason" : "portal-notification-body"} className={cx("mt-1 animate-stepFadeIn rounded-lg border px-2.5 py-2 text-left text-tiny break-words", failure ? "border-status-error-text/40 bg-status-error-bg text-status-error-text" : "border-border bg-background-secondary text-foreground-secondary")}>{detail}</p>;
    $[11] = detail;
    $[12] = failure;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t4 || $[17] !== t5) {
    t6 = <div className="flex max-w-[80%] flex-col"><CollapsibleText text={t1} icon={t2} className={t4}>{t5}</CollapsibleText></div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t0 || $[20] !== t6) {
    t7 = <div className={t0}>{t6}</div>;
    $[19] = t0;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}

// Internal MessageStatusIndicator component
function MessageStatusIndicator() {
  const $ = _c(11);
  const {
    message,
    isLastMessage,
    isInbound
  } = useMessageContext();
  if (!isMessageSummary(message)) {
    return null;
  }
  const shouldShow = isLastMessage || message.status === MessageStatus.FAILED || message.status === MessageStatus.UNDELIVERED;
  if (!shouldShow) {
    return null;
  }
  let t0;
  let t1;
  if ($[0] !== isInbound || $[1] !== message.errorMessage || $[2] !== message.status) {
    const styles = messageStyles({
      isPatient: isInbound
    });
    let t2;
    if ($[5] !== message.errorMessage || $[6] !== message.status) {
      t2 = () => {
        switch (message.status) {
          case MessageStatus.CREATED:
            {
              return <i className="text-foreground-muted">Sending...</i>;
            }
          case MessageStatus.SENT:
            {
              return <span className="flex items-center text-foreground-muted">Sent <FaCheck className="ml-1" /></span>;
            }
          case MessageStatus.READ:
            {
              return <span className="flex items-center text-primary">Seen <BiCheckDouble className="ml-1" /></span>;
            }
          case MessageStatus.DELIVERED:
            {
              return <span className="flex items-center text-foreground-muted">Delivered <BiCheckDouble className="ml-1" /></span>;
            }
          case MessageStatus.UNDELIVERED:
            {
              return <Popover><PopoverTrigger asChild={true}><button type="button" className="flex items-center text-status-warning-text"><FaTriangleExclamation className="mr-1" /> Undelivered</button></PopoverTrigger><PopoverContent className="max-w-xs">{message.errorMessage || "Message was not delivered"}</PopoverContent></Popover>;
            }
          case MessageStatus.FAILED:
            {
              return <Popover><PopoverTrigger asChild={true}><button type="button" className="flex items-center text-status-error-text"><FaTriangleExclamation className="mr-1" /> Failed!</button></PopoverTrigger><PopoverContent className="max-w-xs">{message.errorMessage || "Message failed to send"}</PopoverContent></Popover>;
            }
          default:
            {
              return null;
            }
        }
      };
      $[5] = message.errorMessage;
      $[6] = message.status;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    const renderStatus = t2;
    t0 = styles.statusWrapper();
    t1 = renderStatus();
    $[0] = isInbound;
    $[1] = message.errorMessage;
    $[2] = message.status;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  let t2;
  if ($[8] !== t0 || $[9] !== t1) {
    t2 = <div className={t0}>{t1}</div>;
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

// Props for the main Message component
interface MessageProps {
  message: AnyMessage;
  isLastMessage?: boolean;
  size?: "sm" | "lg";
  onClick?: () => void;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

// Compute if message is inbound (from patient/external).
//
// ``sender`` is null on shape-(c) outbound calls (shared desk phone, no
// owning user) — those are always outbound by direction.
function getIsInbound(message: AnyMessage): boolean {
  if (isEmail(message)) {
    return message.direction === "INBOUND";
  }
  if (isCall(message) && !message.sender) {
    return message.direction === "INBOUND";
  }
  return message.sender?.type === UserType.PATIENT;
}

// Main Message compound component
function MessageRoot(t0) {
  const $ = _c(25);
  const {
    message,
    isLastMessage: t1,
    size: t2,
    onClick,
    children,
    actions
  } = t0;
  const isLastMessage = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "lg" : t2;
  let T0;
  let bubble;
  let isInbound;
  let t3;
  let t4;
  if ($[0] !== actions || $[1] !== children || $[2] !== isLastMessage || $[3] !== message || $[4] !== onClick || $[5] !== size) {
    isInbound = getIsInbound(message);
    const messageIsEmail = isEmail(message);
    const styles = messageStyles({
      size,
      isPatient: isInbound
    });
    const side = isInbound ? "left" : "right";
    let t5;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <MessageHeader />;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    bubble = <ChatBubble side={side} size={size} onClick={onClick} tone={messageIsEmail ? "surface" : undefined} className={cx("min-w-[30%]", messageIsEmail && (isInbound ? "border-l-4 border-l-status-purple-text" : "border-r-4 border-r-status-indigo-text"), actions && (isInbound ? "mr-0" : "ml-0"))}>{t5}<div className={styles.content()}>{children}</div></ChatBubble>;
    T0 = MessageContext.Provider;
    t4 = {
      message,
      isLastMessage,
      size,
      isInbound
    };
    t3 = styles.wrapper();
    $[0] = actions;
    $[1] = children;
    $[2] = isLastMessage;
    $[3] = message;
    $[4] = onClick;
    $[5] = size;
    $[6] = T0;
    $[7] = bubble;
    $[8] = isInbound;
    $[9] = t3;
    $[10] = t4;
  } else {
    T0 = $[6];
    bubble = $[7];
    isInbound = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[12] !== actions || $[13] !== bubble || $[14] !== isInbound) {
    t5 = actions ? <div className={cx("group/msg flex w-full items-center gap-1.5", isInbound ? "justify-start" : "justify-end")}>{!isInbound && actions}{bubble}{isInbound && actions}</div> : bubble;
    $[12] = actions;
    $[13] = bubble;
    $[14] = isInbound;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <PortalNotificationNotice />;
    t7 = <MessageStatusIndicator />;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t3 || $[19] !== t5) {
    t8 = <div className={t3}>{t5}{t6}{t7}</div>;
    $[18] = t3;
    $[19] = t5;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== T0 || $[22] !== t4 || $[23] !== t8) {
    t9 = <T0 value={t4}>{t8}</T0>;
    $[21] = T0;
    $[22] = t4;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}

// Attach subcomponents to create compound component
export const Message = Object.assign(MessageRoot, {
  Text: MessageText,
  Images: MessageImages,
  Files: MessageFiles,
  Call: MessageCall,
  Email: MessageEmailContent,
  ToolResult: MessageToolResult
});
export default Message;